import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadUsers();
    
    // Check for existing session
    const savedUser = localStorage.getItem('laVieSaigonCurrentUser');
    if (savedUser) {
      setCurrentUser(JSON.parse(savedUser));
    }
    
    setIsLoading(false);
  }, []);

  const loadUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, username, email, role, created_at')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const login = async (username, password) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, username, email, role, created_at')
        .eq('username', username)
        .eq('password_hash', password) // In production, use proper password hashing
        .single();

      if (error || !data) {
        return { success: false, error: 'Invalid username or password' };
      }

      setCurrentUser(data);
      localStorage.setItem('laVieSaigonCurrentUser', JSON.stringify(data));
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Login failed' };
    }
  };

  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem('laVieSaigonCurrentUser');
  };

  const addUser = async (userData) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .insert([{
          username: userData.username,
          email: userData.email,
          password_hash: userData.password, // In production, hash the password
          role: userData.role
        }])
        .select()
        .single();

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          return { success: false, error: 'Username or email already exists' };
        }
        throw error;
      }

      await loadUsers();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to add user' };
    }
  };

  const deleteUser = async (userId) => {
    if (userId === currentUser?.id) {
      return { success: false, error: 'Cannot delete current user' };
    }

    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', userId);

      if (error) throw error;
      
      await loadUsers();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to delete user' };
    }
  };

  const updateUser = async (userId, userData) => {
    try {
      const updateData = {
        username: userData.username,
        email: userData.email,
        role: userData.role,
        updated_at: new Date().toISOString()
      };

      if (userData.password) {
        updateData.password_hash = userData.password; // In production, hash the password
      }

      const { error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', userId);

      if (error) throw error;
      
      await loadUsers();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to update user' };
    }
  };

  return (
    <AuthContext.Provider value={{
      currentUser,
      users,
      isLoading,
      login,
      logout,
      addUser,
      deleteUser,
      updateUser,
      isAdmin: currentUser?.role === 'admin'
    }}>
      {children}
    </AuthContext.Provider>
  );
};